import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Check, Loader2, Sparkles, Crown } from 'lucide-react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { axiosClient } from '@/api/axiosClient';
import { queryKeys } from '@/api/queryKeys';
import { getPlanFeatures, isYearlyPlan } from '@/config/plans';
import toast from 'react-hot-toast';
import { clearRecommendedPlan } from '@/lib/recommendedPlan';

interface PostAuthPlanModalProps {
  isOpen: boolean;
  onContinueFree: () => void;
  recommendedPlan?: string;
}

interface HubPlan {
  id: number | string;
  name: string;
  slug: string;
  description: string;
  price: number | string;
  billing_period: 'MONTHLY' | 'YEARLY';
  features: Record<string, boolean | number>;
  limits: Record<string, number>;
  is_popular?: boolean;
}

interface PlansResponse {
  plans: HubPlan[];
}

export default function PostAuthPlanModal({
  isOpen,
  onContinueFree,
  recommendedPlan,
}: PostAuthPlanModalProps) {
  const { t } = useTranslation();

  // Auto-detect billing period from recommendedPlan slug
  const getInitialBillingPeriod = (): 'MONTHLY' | 'YEARLY' => {
    if (recommendedPlan) {
      if (recommendedPlan.includes('yearly') || recommendedPlan.includes('annual')) {
        return 'YEARLY';
      }
    }
    return 'MONTHLY';
  };

  const [billingPeriod, setBillingPeriod] = useState<'MONTHLY' | 'YEARLY'>(
    getInitialBillingPeriod
  );
  const [loadingPlan, setLoadingPlan] = useState<string | null>(null);

  // Reset billing period when recommendedPlan changes
  useEffect(() => {
    setBillingPeriod(getInitialBillingPeriod());
  }, [recommendedPlan]);

  // Fetch plans from Hub
  const { data: plansData, isLoading, refetch } = useQuery({
    queryKey: [...queryKeys.pricing, 'hub'],
    queryFn: async () => {
      const { data } = await axiosClient.get<PlansResponse>('/hub/billing/plans');
      return data;
    },
    enabled: isOpen,
  });

  // Checkout mutation
  const checkoutMutation = useMutation({
    mutationFn: async (planSlug: string) => {
      const { data } = await axiosClient.post<{ url: string }>(
        '/hub/billing/checkout',
        {
          plan_slug: planSlug,
          success_url: `${window.location.origin}/dashboard?checkout=success`,
          cancel_url: `${window.location.origin}/dashboard?checkout=canceled`,
        }
      );
      return data;
    },
    onSuccess: (data) => {
      if (data.url) {
        clearRecommendedPlan();
        window.location.href = data.url;
      }
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Falha ao iniciar checkout');
      setLoadingPlan(null);
    },
  });

  if (!isOpen) return null;

  // Filter plans by billing period and exclude free, sorted by price
  const paidPlans =
    plansData?.plans
      .filter(
        (plan) => plan.billing_period === billingPeriod && !plan.slug.includes('free')
      )
      .sort((a, b) => {
        const priceA = typeof a.price === 'string' ? parseFloat(a.price) : a.price;
        const priceB = typeof b.price === 'string' ? parseFloat(b.price) : b.price;
        return priceA - priceB;
      }) || [];

  // Determine which plan to highlight
  const getRecommendedPlanSlug = (): string | null => {
    if (recommendedPlan) {
      // Extract base plan name and match with current billing period
      const basePlan = recommendedPlan
        .replace('-monthly', '')
        .replace('-yearly', '')
        .replace('-annual', '');
      const targetSlug = `${basePlan}-${billingPeriod.toLowerCase()}`;
      // Check if plan exists
      const exists = paidPlans.some((p) => p.slug === targetSlug);
      if (exists) return targetSlug;
      // Fallback: check if exact match exists
      if (paidPlans.some((p) => p.slug === recommendedPlan)) return recommendedPlan;
    }
    // Default: most expensive plan
    return paidPlans.length > 0 ? paidPlans[paidPlans.length - 1].slug : null;
  };

  const recommendedPlanSlug = getRecommendedPlanSlug();

  const handleSubscribe = (plan: HubPlan) => {
    setLoadingPlan(plan.slug);
    checkoutMutation.mutate(plan.slug);
  };

  const formatPrice = (price: number | string): string => {
    const numPrice = typeof price === 'string' ? parseFloat(price) : price;
    return `R$${numPrice.toFixed(2).replace('.', ',')}`;
  };

  const getMonthlyEquivalent = (plan: HubPlan): string | null => {
    if (plan.billing_period === 'YEARLY') {
      const price = typeof plan.price === 'string' ? parseFloat(plan.price) : plan.price;
      const monthly = price / 12;
      return `R$${monthly.toFixed(2).replace('.', ',')}/mes`;
    }
    return null;
  };

  const getFeatures = (plan: HubPlan): string[] => {
    const featureKeys = getPlanFeatures(plan.slug);
    const features = featureKeys.map((key) => t(key));
    if (isYearlyPlan(plan.slug)) {
      features.push(t('billing.features.twoMonthsFree'));
    }
    return features;
  };

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop - no onClick handler */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />

      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-4xl overflow-hidden max-h-[90vh] flex flex-col">
        {/* Header - no X button */}
        <div className="p-6 border-b border-border bg-gradient-to-r from-primary to-primary-hover">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-white flex items-center justify-center gap-2">
              <Crown className="w-6 h-6" />
              Escolha o melhor plano para voce
            </h2>
            <p className="text-white/80 mt-1">
              Desbloqueie todo o potencial do Content4Life
            </p>
          </div>
        </div>

        {/* Billing Toggle */}
        <div className="flex justify-center p-6 bg-secondary pb-0">
          <div className="bg-border p-1 rounded-lg flex">
            <button
              onClick={() => setBillingPeriod('MONTHLY')}
              className={`px-6 py-2.5 rounded-md text-sm font-medium transition-all ${
                billingPeriod === 'MONTHLY'
                  ? 'bg-white shadow text-foreground'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              Mensal
            </button>
            <button
              onClick={() => setBillingPeriod('YEARLY')}
              className={`px-6 py-2.5 rounded-md text-sm font-medium transition-all flex items-center gap-2 ${
                billingPeriod === 'YEARLY'
                  ? 'bg-white shadow text-foreground'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              Anual
              <span className="bg-accent text-white text-xs px-2 py-0.5 rounded-full font-bold">
                2 meses gratis
              </span>
            </button>
          </div>
        </div>

        {/* Plans */}
        <div className="p-6 overflow-y-auto bg-secondary flex-1">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
              <span className="ml-2 text-muted-foreground">Carregando planos...</span>
            </div>
          ) : plansData ? (
            <div className="grid md:grid-cols-2 gap-6 max-w-3xl mx-auto">
              {paidPlans.map((plan) => {
                const monthlyEquivalent = getMonthlyEquivalent(plan);
                const isYearly = plan.billing_period === 'YEARLY';
                const isRecommended = plan.slug === recommendedPlanSlug;

                return (
                  <div
                    key={plan.slug}
                    className={`relative bg-white rounded-xl p-6 flex flex-col ${
                      isRecommended
                        ? 'shadow-lg border-2 border-primary ring-2 ring-primary/20'
                        : 'shadow-sm border border-border'
                    }`}
                  >
                    {isRecommended && (
                      <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                        <span className="bg-primary text-white text-xs font-bold px-4 py-1 rounded-full">
                          RECOMENDADO
                        </span>
                      </div>
                    )}

                    <div
                      className={`flex items-center gap-2 mb-2 ${isRecommended ? 'mt-2' : ''}`}
                    >
                      <Crown
                        className={`w-5 h-5 ${isRecommended ? 'text-primary' : 'text-muted-foreground'}`}
                      />
                      <h3 className="text-lg font-bold text-foreground">{plan.name}</h3>
                    </div>
                    <p className="text-sm text-muted-foreground">{plan.description}</p>

                    <div className="mt-4 flex items-baseline text-foreground">
                      {isYearly && monthlyEquivalent ? (
                        <>
                          <span
                            className={`text-4xl font-extrabold tracking-tight ${
                              isRecommended ? 'text-primary' : 'text-foreground'
                            }`}
                          >
                            {monthlyEquivalent.replace('/mes', '')}
                          </span>
                          <span className="ml-1 text-xl font-semibold text-muted-foreground">
                            /mes
                          </span>
                        </>
                      ) : (
                        <>
                          <span
                            className={`text-4xl font-extrabold tracking-tight ${
                              isRecommended ? 'text-primary' : 'text-foreground'
                            }`}
                          >
                            {formatPrice(plan.price)}
                          </span>
                          <span className="ml-1 text-xl font-semibold text-muted-foreground">
                            /mes
                          </span>
                        </>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">
                      {isYearly
                        ? `Cobrado ${formatPrice(plan.price)} anualmente`
                        : 'Cobrado mensalmente'}
                    </p>

                    <ul className="mt-6 space-y-3 flex-1">
                      {getFeatures(plan).map((feature, idx) => (
                        <li key={idx} className="flex">
                          <Check className="flex-shrink-0 w-5 h-5 text-accent" />
                          <span className="ml-3 text-sm text-foreground font-medium">
                            {feature}
                          </span>
                        </li>
                      ))}
                    </ul>

                    <button
                      onClick={() => handleSubscribe(plan)}
                      disabled={!!loadingPlan}
                      className={`mt-8 w-full py-3 px-4 rounded-lg text-sm font-bold text-white transition-all flex items-center justify-center ${
                        isRecommended
                          ? 'bg-primary hover:bg-primary-hover shadow-lg shadow-primary/30'
                          : 'bg-slate-900 hover:bg-slate-800'
                      } disabled:opacity-50`}
                    >
                      {loadingPlan === plan.slug ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin mr-2" />
                          Processando...
                        </>
                      ) : (
                        <>
                          <Sparkles className="w-4 h-4 mr-2" />
                          Assinar Agora
                        </>
                      )}
                    </button>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-12">
              <p className="text-muted-foreground">
                Nao foi possivel carregar os planos. Tente novamente.
              </p>
              <button
                onClick={() => refetch()}
                className="mt-4 px-4 py-2 bg-slate-900 text-white rounded-lg hover:bg-slate-800 transition-colors"
              >
                Tentar novamente
              </button>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 bg-secondary border-t border-border text-center space-y-2">
          <button
            onClick={onContinueFree}
            className="text-sm text-muted-foreground hover:text-foreground transition-colors underline underline-offset-2"
          >
            Quero continuar no gratuito
          </button>
          <p className="text-xs text-muted-foreground">
            Pagamento seguro via Stripe. Cancele quando quiser.
          </p>
        </div>
      </div>
    </div>,
    document.body
  );
}
